// Busca de endereço por CEP — ViaCEP (base dos Correios, sem chave, CORS liberado)
// com fallback para BrasilAPI. Portado de Vendeon (web/src/lib/cep.ts). Uso no
// browser do cliente durante o checkout de entrega.
export interface CepResult {
  cep: string;
  logradouro: string;
  bairro: string;
  cidade: string;
  uf: string;
}

/** Só dígitos; CEP válido tem 8. */
export function cepDigits(v: string): string {
  return (v ?? '').replace(/\D/g, '').slice(0, 8);
}

export function cepValido(v: string): boolean {
  return cepDigits(v).length === 8;
}

/** Máscara 00000-000. */
export function formatCep(v: string): string {
  const d = cepDigits(v);
  return d.length > 5 ? `${d.slice(0, 5)}-${d.slice(5)}` : d;
}

/**
 * QA-28 B-01 — as duas APIs de CEP são de TERCEIROS e o `fetch` não tinha timeout: com a rede pendurada (Wi-Fi
 * de restaurante, DNS lento) a promessa nunca resolvia, o rótulo do campo ficava "CEP · buscando…" para sempre
 * e o cliente não sabia que podia preencher na mão. `AbortController` + timer cortam em TIMEOUT_MS e o
 * chamador cai no caminho de erro que já existe ("preencha o endereço manualmente").
 * Não usa `AbortSignal.timeout` para não depender de suporte do runtime/jsdom.
 */
const TIMEOUT_MS = 6000;

async function fetchComTimeout(url: string): Promise<Response> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  try {
    return await fetch(url, { signal: ctrl.signal });
  } finally {
    clearTimeout(timer);
  }
}

async function viaCep(cep: string): Promise<CepResult | null> {
  const res = await fetchComTimeout(`https://viacep.com.br/ws/${cep}/json/`);
  if (!res.ok) return null;
  const d = (await res.json()) as {
    erro?: boolean;
    cep?: string;
    logradouro?: string;
    bairro?: string;
    localidade?: string;
    uf?: string;
  };
  if (d.erro) return null;
  return {
    cep: d.cep ?? cep,
    logradouro: d.logradouro ?? '',
    bairro: d.bairro ?? '',
    cidade: d.localidade ?? '',
    uf: d.uf ?? '',
  };
}

async function brasilApi(cep: string): Promise<CepResult | null> {
  const res = await fetchComTimeout(`https://brasilapi.com.br/api/cep/v1/${cep}`);
  if (!res.ok) return null;
  const d = (await res.json()) as {
    cep?: string;
    street?: string;
    neighborhood?: string;
    city?: string;
    state?: string;
  };
  return {
    cep: d.cep ?? cep,
    logradouro: d.street ?? '',
    bairro: d.neighborhood ?? '',
    cidade: d.city ?? '',
    uf: d.state ?? '',
  };
}

/**
 * Busca o endereço de um CEP. Retorna null se inválido ou não encontrado.
 * Tenta ViaCEP e, em falha de rede, BrasilAPI.
 */
export async function buscarCep(cepRaw: string): Promise<CepResult | null> {
  const cep = cepDigits(cepRaw);
  if (cep.length !== 8) return null;
  try {
    const r = await viaCep(cep);
    if (r) return r;
  } catch {
    /* tenta fallback */
  }
  try {
    return await brasilApi(cep);
  } catch {
    return null;
  }
}
